import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Users, Send, MailOpen, Workflow, Calendar as CalendarIcon, BookOpen,
  ArrowUpRight, TrendingUp, TrendingDown, Minus,
  MousePointerClick, Share2, Inbox as InboxIcon, ChevronRight,
  Zap, Activity, Sparkles, CreditCard, Star, Layers, Globe, DollarSign,
  MapPin, Plus, Check, ChevronDown, Building2, Search, Bell, Settings as SettingsIcon,
  Download, Sun, Moon, Clock, MoreHorizontal, Trash2, Edit2, CheckCircle2,
  ListTodo, UserCheck, FolderPlus, RefreshCw, Share, ArrowRight, ExternalLink
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {
  AreaChart, Area, BarChart, Bar, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";
import api from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLocationContext } from "@/context/LocationContext";
import { useTheme } from "@/context/ThemeContext";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { locations, activeLocation, switchLocation, createLocation } = useLocationContext();
  const { mode, setMode } = useTheme();

  // Location Creation state
  const [createLocOpen, setCreateLocOpen] = useState(false);
  const [locForm, setLocForm] = useState({
    name: "",
    city: "",
    state: "",
    category: "Commercial Photography & Media",
    address: "",
    phone: "",
  });

  // Dialog States for Reference Cards from Image
  const [addBoardOpen, setAddBoardOpen] = useState(false);
  const [boardTitle, setBoardTitle] = useState("");
  const [boardType, setBoardType] = useState("Marketing Campaigns");

  const [stayOrganizedOpen, setStayOrganizedOpen] = useState(false);
  const [syncNotesOpen, setSyncNotesOpen] = useState(false);
  const [collaborateOpen, setCollaborateOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const [addAssignmentOpen, setAddAssignmentOpen] = useState(false);
  const [assignmentTitle, setAssignmentTitle] = useState("");
  const [assignmentTag, setAssignmentTag] = useState("Package design");
  const [assignmentPriority, setAssignmentPriority] = useState("High");

  // Notifications State
  const [notifications, setNotifications] = useState([
    {
      id: "notif_1",
      title: "Upcoming event",
      tag: "Time: 120 min",
      time: "Sat, 10 May · 11:00 AM - 11:45 AM",
      active: true,
    },
    {
      id: "notif_2",
      title: "Message | Product design",
      tag: "Direct Message",
      time: "Hey team, just wanted to check in and see how the new campaign is going...",
      active: true,
    }
  ]);

  // Assignments State
  const [assignments, setAssignments] = useState([
    {
      id: "as_1",
      title: "Design a packaging concept for a new product",
      category: "Package design",
      priority: "High",
      assignee: "Rachel Lee",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&auto=format&fit=crop&q=80",
    }
  ]);

  // Today Tasks State
  const [tasks, setTasks] = useState([
    {
      id: "t_1",
      title: "Conduct research",
      date: "4 May, 09:20 AM",
      duration: "02 h 45 m",
      progress: 90,
      completed: false,
    },
    {
      id: "t_2",
      title: "Schedule a meeting",
      date: "14 May, 12:45 AM",
      duration: "06 h 55 m",
      progress: 50,
      completed: false,
    },
    {
      id: "t_3",
      title: "Send out reminders",
      date: "21 May, 10:30 AM",
      duration: "01 h 30 m",
      progress: 10,
      completed: false,
    }
  ]);

  // Date strip active index (default to 4 = Fri 18)
  const [activeDateIndex, setActiveDateIndex] = useState(4);
  const daysOfWeek = [
    { day: "Mon", date: 14 },
    { day: "Tue", date: 15 },
    { day: "Wed", date: 16 },
    { day: "Thr", date: 17 },
    { day: "Fri", date: 18 },
    { day: "Sat", date: 19 },
    { day: "Sun", date: 20 },
  ];

  // Search input state
  const [searchCmd, setSearchCmd] = useState("");

  // Stats
  const [stats, setStats] = useState({
    contacts_count: 1420,
    campaigns_count: 8,
    emails_sent: 18450,
    open_rate: 42.8,
    click_rate: 18.4,
    automations_active: 6,
    pipeline_value: 38450,
    chart: [
      { name: "Mon", emails: 240, opens: 180, clicks: 90 },
      { name: "Tue", emails: 480, opens: 320, clicks: 140 },
      { name: "Wed", emails: 620, opens: 410, clicks: 210 },
      { name: "Thu", emails: 890, opens: 580, clicks: 310 },
      { name: "Fri", emails: 740, opens: 490, clicks: 260 },
      { name: "Sat", emails: 310, opens: 210, clicks: 110 },
      { name: "Sun", emails: 450, opens: 310, clicks: 150 },
    ],
    total_tracked: 3730
  });

  useEffect(() => {
    api.get("/dashboard/stats")
      .then(({ data }) => { if (data) setStats(data); })
      .catch(() => {});
  }, []);

  const handleCreateDashboardLocation = async (e) => {
    e.preventDefault();
    if (!locForm.name || !locForm.city || !locForm.state) {
      toast.error("Location name, city, and state are required");
      return;
    }
    try {
      await createLocation(locForm);
      setCreateLocOpen(false);
      setLocForm({ name: "", city: "", state: "", category: "Commercial Photography & Media", address: "", phone: "" });
    } catch {}
  };

  const handleAddBoard = (e) => {
    e.preventDefault();
    if (!boardTitle.trim()) {
      toast.error("Please enter a board title");
      return;
    }
    toast.success(`Board "${boardTitle}" created successfully!`);
    setAddBoardOpen(false);
    setBoardTitle("");
  };

  const handleAddAssignment = (e) => {
    e.preventDefault();
    if (!assignmentTitle.trim()) {
      toast.error("Please enter assignment title");
      return;
    }
    setAssignments((prev) => [
      {
        id: `as_${Date.now()}`,
        title: assignmentTitle,
        category: assignmentTag,
        priority: assignmentPriority,
        assignee: user?.name || "Rachel Lee",
        avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=120&auto=format&fit=crop&q=80",
      },
      ...prev,
    ]);
    toast.success("Assignment added to workspace board");
    setAddAssignmentOpen(false);
    setAssignmentTitle("");
  };

  const handleExportData = () => {
    toast.success("Exporting workspace reports to .xls file…");
    const blob = new Blob([
      "Date,Campaign,Contacts,OpenRate,ClickRate\n2024-05-18,Agency Newsletter,1420,42.8%,18.4%"
    ], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `LaunchDeck_Dashboard_${new Date().toISOString().slice(0, 10)}.xls`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const firstName = (user?.name || "").split(" ")[0] || "James";

  return (
    <div className="min-h-screen bg-[#F6F7FB] dark:bg-[#0B0F19] text-slate-900 dark:text-slate-100 p-4 sm:p-8 font-sans space-y-6" data-testid="dashboard-page">

      {/* ── Top Header Navigation Bar Matching Reference Image Exactly ── */}
      <div className="bg-white dark:bg-[#111827] rounded-3xl p-4 sm:px-6 sm:py-3.5 shadow-sm border border-slate-100 dark:border-slate-800 flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Left: Tab Links */}
        <div className="flex items-center gap-6 sm:gap-8 self-start md:self-center">
          <button className="text-sm font-bold text-slate-900 dark:text-white pb-1 border-b-2 border-indigo-600 dark:border-indigo-400 cursor-pointer flex items-center gap-1.5">
            <span className="grid grid-cols-2 gap-0.5 w-3.5 h-3.5">
              <span className="bg-indigo-600 rounded-[1px]" />
              <span className="bg-indigo-600 rounded-[1px]" />
              <span className="bg-indigo-600 rounded-[1px]" />
              <span className="bg-indigo-600 rounded-[1px]" />
            </span>
            <span>Dashboard</span>
          </button>
          <Link to="/automations" className="text-sm font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors flex items-center gap-1.5">
            <Workflow className="w-4 h-4 text-slate-400" />
            <span>Workflows</span>
          </Link>
          <Link to="/marketplace" className="text-sm font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors flex items-center gap-1.5">
            <Layers className="w-4 h-4 text-slate-400" />
            <span>Integrations</span>
          </Link>
        </div>

        {/* Center: Search Command Bar */}
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
          <Input
            value={searchCmd}
            onChange={(e) => setSearchCmd(e.target.value)}
            placeholder="Search or type command"
            className="w-full pl-10 pr-4 h-9 text-xs rounded-full bg-slate-50 dark:bg-slate-800/60 border-slate-200/80 dark:border-slate-700 focus-visible:ring-indigo-500"
          />
        </div>

        {/* Right: Theme Toggle, Notifications, Settings & Actions */}
        <div className="flex items-center gap-3 self-end md:self-center">
          {/* Light / Dark Mode Pill Toggle */}
          <div className="inline-flex items-center p-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
            <button
              onClick={() => setMode("light")}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                mode === "light"
                  ? "bg-white text-indigo-600 shadow-xs"
                  : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Sun className="w-3 h-3" />
              <span>Light</span>
            </button>
            <button
              onClick={() => setMode("dark")}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition-all ${
                mode === "dark"
                  ? "bg-indigo-600 text-white shadow-xs"
                  : "text-slate-500 hover:text-slate-300"
              }`}
            >
              <Moon className="w-3 h-3" />
              <span>Dark</span>
            </button>
          </div>

          {/* Bell Icon */}
          <button
            onClick={() => toast.info("No unread system alerts.")}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <Bell className="w-4 h-4" />
          </button>

          {/* Settings Gear */}
          <Link
            to="/settings"
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <SettingsIcon className="w-4 h-4" />
          </Link>

          {/* Export Data Button */}
          <Button
            onClick={handleExportData}
            variant="outline"
            size="sm"
            className="h-9 px-3.5 rounded-full text-xs font-semibold gap-1.5 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <Download className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
            <span>Export data</span>
            <span className="text-[10px] text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-950/60 px-1.5 py-0.2 rounded-md">.xls</span>
          </Button>

          {/* Add New Board Modal Trigger */}
          <Button
            onClick={() => setAddBoardOpen(true)}
            className="h-9 px-4 rounded-full text-xs font-bold bg-[#14172B] hover:bg-[#1E2342] text-white shadow-xs gap-1.5 dark:bg-indigo-600 dark:hover:bg-indigo-700"
          >
            <span>Add new board</span>
          </Button>
        </div>
      </div>

      {/* ── Welcome Heading Section ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-1">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Hi, {firstName}!
            </h1>
            <div className="flex -space-x-1.5">
              <span className="w-7 h-7 rounded-full bg-indigo-100 border-2 border-white dark:border-slate-900 flex items-center justify-center text-indigo-600 text-xs font-bold">
                👤
              </span>
              <span className="w-7 h-7 rounded-full bg-blue-100 border-2 border-white dark:border-slate-900 flex items-center justify-center text-blue-600 text-xs font-bold">
                🎯
              </span>
            </div>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
            What are your plans for today?
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
            This platform is designed to revolutionize the way you organize and access your notes &amp; marketing engine.
          </p>
        </div>
      </div>

      {/* ── Top 4 Quick-Action Feature Cards (From Mockup Image) ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {/* Card 1: Dashed New Board Card */}
        <div
          onClick={() => setAddBoardOpen(true)}
          className="rounded-3xl border-2 border-dashed border-indigo-200 dark:border-indigo-900/60 bg-[#EEF2FF]/40 dark:bg-indigo-950/20 p-6 flex flex-col items-center justify-center min-h-[160px] cursor-pointer hover:bg-[#EEF2FF]/80 transition-all group"
        >
          <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
            <Plus className="w-6 h-6 stroke-[2.5]" />
          </div>
          <span className="text-xs font-bold text-indigo-700 dark:text-indigo-300 mt-3">
            Add New Board
          </span>
        </div>

        {/* Card 2: Stay organized */}
        <div
          onClick={() => setStayOrganizedOpen(true)}
          className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 flex flex-col items-center text-center justify-between shadow-xs hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="w-16 h-16 flex items-center justify-center mb-2">
            <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none">
              <rect x="12" y="14" width="38" height="42" rx="4" stroke="#4F46E5" strokeWidth="2.5" fill="#EEF2FF" />
              <path d="M12 24H50" stroke="#4F46E5" strokeWidth="2" />
              <circle cx="20" cy="19" r="1.5" fill="#4F46E5" />
              <circle cx="28" cy="19" r="1.5" fill="#4F46E5" />
              <circle cx="36" cy="19" r="1.5" fill="#4F46E5" />
              <rect x="18" y="30" width="12" height="12" rx="2" stroke="#4F46E5" strokeWidth="2" fill="#818CF8" />
              <path d="M36 34L44 42" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
              <path d="M44 34L36 42" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h4 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 transition-colors">
              Stay organized
            </h4>
            <p className="text-xs text-slate-400 mt-0.5">A clear structure for your notes</p>
          </div>
        </div>

        {/* Card 3: Sync your notes */}
        <div
          onClick={() => setSyncNotesOpen(true)}
          className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 flex flex-col items-center text-center justify-between shadow-xs hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="w-16 h-16 flex items-center justify-center mb-2">
            <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none">
              <path d="M8 18Q32 24 56 18" stroke="#4F46E5" strokeWidth="2" strokeLinecap="round" />
              {/* Note 1 */}
              <rect x="12" y="24" width="16" height="22" rx="2" stroke="#4F46E5" strokeWidth="2" fill="#FEF3C7" transform="rotate(-6 20 35)" />
              <line x1="20" y1="18" x2="20" y2="24" stroke="#4F46E5" strokeWidth="2" />
              {/* Note 2 */}
              <rect x="36" y="24" width="16" height="22" rx="2" stroke="#4F46E5" strokeWidth="2" fill="#E0E7FF" transform="rotate(6 44 35)" />
              <line x1="44" y1="18" x2="44" y2="24" stroke="#4F46E5" strokeWidth="2" />
            </svg>
          </div>
          <div>
            <h4 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 transition-colors">
              Sync your notes
            </h4>
            <p className="text-xs text-slate-400 mt-0.5">Ensure that notes are synced</p>
          </div>
        </div>

        {/* Card 4: Collaborate and share */}
        <div
          onClick={() => setCollaborateOpen(true)}
          className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 flex flex-col items-center text-center justify-between shadow-xs hover:shadow-md transition-all cursor-pointer group"
        >
          <div className="w-16 h-16 flex items-center justify-center mb-2">
            <svg viewBox="0 0 64 64" className="w-14 h-14" fill="none">
              <rect x="10" y="18" width="44" height="30" rx="4" stroke="#4F46E5" strokeWidth="2.5" fill="#EEF2FF" />
              <circle cx="22" cy="33" r="5" stroke="#4F46E5" strokeWidth="2" fill="white" />
              <circle cx="42" cy="33" r="5" stroke="#4F46E5" strokeWidth="2" fill="white" />
              <rect x="22" y="30" width="20" height="6" fill="#818CF8" />
              <rect x="16" y="22" width="32" height="3" rx="1.5" fill="#4F46E5" />
            </svg>
          </div>
          <div>
            <h4 className="text-base font-bold text-slate-900 dark:text-white group-hover:text-indigo-600 transition-colors">
              Collaborate and share
            </h4>
            <p className="text-xs text-slate-400 mt-0.5">Share notes with colleagues</p>
          </div>
        </div>
      </div>

      {/* ── Main Bento Grid Layout Matching Mockup ── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* ── COLUMN 1: Notifications & Today tasks (5 Cols) ── */}
        <div className="lg:col-span-4 space-y-6">

          {/* Notifications Card */}
          <div className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Notifications</h3>
              <button
                onClick={() => {
                  setNotifications([]);
                  toast.success("Notifications cleared");
                }}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                Clear
              </button>
            </div>

            {notifications.length === 0 ? (
              <div className="text-center py-6 text-xs text-slate-400">All notifications caught up!</div>
            ) : (
              <div className="space-y-3">
                {/* Event 1 (Float Card with green dot) */}
                <div className="rounded-2xl p-4 bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-md relative">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold text-slate-900 dark:text-white">Upcoming event</span>
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    </div>
                    <span className="text-[10px] text-slate-400">Time: 120 min</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 text-xs text-slate-500 dark:text-slate-400">
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon className="w-3.5 h-3.5 text-slate-400" />
                      <span>Sat, 10 May</span>
                      <span>·</span>
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      <span>11 AM - 11:45 AM</span>
                    </div>
                    <button
                      onClick={() => setNotifications((prev) => prev.filter((_, i) => i !== 0))}
                      className="text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Message Item */}
                <div className="rounded-2xl p-4 bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800">
                  <div className="text-xs font-bold text-slate-800 dark:text-slate-200">
                    Message | Product design
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 leading-relaxed">
                    Hey team, just wanted to check in and see how the new marketing rollout and automated pipelines are progressing...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Today Tasks Card */}
          <div className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Today tasks</h3>
                <div className="flex -space-x-1.5">
                  <span className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-[10px] font-bold border-2 border-white dark:border-slate-900">
                    R
                  </span>
                  <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-[10px] font-bold border-2 border-white dark:border-slate-900">
                    J
                  </span>
                  <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-[10px] font-bold border-2 border-white dark:border-slate-900">
                    +
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs text-slate-400">
                <button onClick={() => toast.info("Task editing mode active")} className="hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1">
                  <Edit2 className="w-3.5 h-3.5" />
                  <span>Edit</span>
                </button>
                <button onClick={() => toast.success("Task list link copied to clipboard")} className="hover:text-slate-700 dark:hover:text-slate-200 flex items-center gap-1">
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </button>
              </div>
            </div>

            {/* Task list with progress bars */}
            <div className="space-y-4">
              {tasks.map((t) => (
                <div key={t.id} className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-bold text-slate-900 dark:text-white">{t.title}</div>
                      <div className="text-[10px] text-slate-400 mt-0.5">{t.date}</div>
                    </div>
                    <div className="text-right">
                      <span className="text-[11px] font-bold text-indigo-600 dark:text-indigo-400">{t.progress}%</span>
                      <div className="text-[10px] text-slate-400">{t.duration}</div>
                    </div>
                  </div>
                  {/* Progress Line */}
                  <div className="w-full h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-600 transition-all duration-500"
                      style={{ width: `${t.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── COLUMN 2: Assignments & Go Premium (4 Cols) ── */}
        <div className="lg:col-span-4 space-y-6">

          {/* Assignments Card */}
          <div className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white">Assignments</h3>
              <button
                onClick={() => setAddAssignmentOpen(true)}
                className="text-xs font-semibold text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 flex items-center gap-1"
              >
                <Edit2 className="w-3.5 h-3.5" />
                <span>Edit</span>
              </button>
            </div>

            {/* Category Chips */}
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900">
                Motion design
              </span>
              <span className="px-3 py-1 rounded-full text-[11px] font-semibold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                Logo
              </span>
            </div>

            {/* Featured Assignment Card */}
            {assignments.map((as) => (
              <div key={as.id} className="p-5 rounded-2xl bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 shadow-sm space-y-4">
                <div className="flex items-start justify-between gap-2">
                  <h4 className="text-sm font-bold text-slate-900 dark:text-white leading-snug">
                    {as.title}
                  </h4>
                  <Badge className="bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 text-[10px] font-bold shrink-0">
                    {as.priority}
                  </Badge>
                </div>

                <div className="flex items-center justify-between pt-2">
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                    {as.category}
                  </span>
                  <div className="flex items-center gap-2">
                    <img
                      src={as.avatar}
                      alt={as.assignee}
                      className="w-6 h-6 rounded-full object-cover border border-slate-200"
                    />
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                      {as.assignee}
                    </span>
                  </div>
                </div>
              </div>
            ))}

            {/* + Add New Assignment Button */}
            <Button
              onClick={() => setAddAssignmentOpen(true)}
              variant="outline"
              className="w-full h-10 rounded-2xl border-dashed border-indigo-300 dark:border-indigo-800 bg-indigo-50/50 dark:bg-indigo-950/20 text-indigo-600 dark:text-indigo-400 text-xs font-bold gap-2"
            >
              <Plus className="w-4 h-4" />
              <span>Add new assignment</span>
            </Button>
          </div>

          {/* Go Premium! Purple Card (Matching Reference Screenshot) */}
          <div className="rounded-3xl p-6 sm:p-7 bg-gradient-to-br from-[#5D55EA] via-[#4F46E5] to-[#3730A3] text-white shadow-xl shadow-indigo-500/20 flex flex-col justify-between space-y-4">
            <div className="w-full flex justify-center py-2">
              <div className="relative">
                {/* Cute Gift Box Illustration */}
                <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-4xl shadow-inner">
                  🎁
                </div>
                <Sparkles className="w-5 h-5 text-amber-300 absolute -top-1 -right-1 animate-pulse" />
              </div>
            </div>

            <div className="text-center space-y-1.5">
              <h3 className="text-xl font-black tracking-tight text-white">Go premium!</h3>
              <p className="text-xs text-indigo-100 leading-relaxed max-w-xs mx-auto">
                Gain access to a range of benefits designed to enhance your user experience, unlimited pipelines &amp; sub-accounts.
              </p>
            </div>

            <Link to="/billing" className="w-full">
              <Button className="w-full h-10 rounded-full bg-[#181829] hover:bg-black text-white text-xs font-bold shadow-md">
                Find out more
              </Button>
            </Link>
          </div>
        </div>

        {/* ── COLUMN 3: Schedule, Gauges, Board meeting (4 Cols) ── */}
        <div className="lg:col-span-4 space-y-6">

          {/* Calendar Strip Card (May 2021 / Current Month) */}
          <div className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white">May 2024</h3>
              <div className="flex items-center gap-1 text-slate-400">
                <button onClick={() => toast.info("Previous week")} className="w-6 h-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center">
                  &lt;
                </button>
                <button onClick={() => toast.info("Next week")} className="w-6 h-6 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 flex items-center justify-center">
                  &gt;
                </button>
              </div>
            </div>

            {/* Horizontal Day Selector */}
            <div className="flex items-center justify-between">
              {daysOfWeek.map((d, i) => {
                const isActive = activeDateIndex === i;
                return (
                  <div
                    key={i}
                    onClick={() => setActiveDateIndex(i)}
                    className="flex flex-col items-center gap-1.5 cursor-pointer"
                  >
                    <span className="text-[10px] font-bold text-slate-400">{d.day}</span>
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                        isActive
                          ? "bg-indigo-600 text-white shadow-md scale-110"
                          : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      {d.date}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-3">
              <div className="text-[11px] font-bold text-slate-400">04:30 - 05:00 PM</div>
              <div className="p-3 rounded-2xl bg-indigo-50/70 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-900 flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">Team meeting</div>
                  <div className="text-[10px] text-slate-400">12:00 - 12:30 | UX / UI design</div>
                </div>
                <MoreHorizontal className="w-4 h-4 text-slate-400 cursor-pointer" />
              </div>

              <div className="text-[11px] font-bold text-slate-400">11:30 - 12:30 PM</div>
              <div className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-100 dark:border-slate-800 flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-slate-900 dark:text-white">Meeting with new client</div>
                  <div className="text-[10px] text-slate-400">12:30 - 01:30 PM | Onboarding</div>
                </div>
                <MoreHorizontal className="w-4 h-4 text-slate-400 cursor-pointer" />
              </div>
            </div>
          </div>

          {/* Circular Gauges Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Gauge 1: 90% */}
            <div className="p-4 rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col items-center text-center space-y-2">
              <div className="relative w-14 h-14 flex items-center justify-center">
                <svg className="w-14 h-14 -rotate-90">
                  <circle cx="28" cy="28" r="22" stroke="currentColor" strokeWidth="4" className="text-slate-100 dark:text-slate-800" fill="transparent" />
                  <circle cx="28" cy="28" r="22" stroke="#10B981" strokeWidth="4" strokeDasharray="138" strokeDashoffset="14" strokeLinecap="round" fill="transparent" />
                </svg>
                <span className="absolute text-xs font-black text-slate-900 dark:text-white">90%</span>
              </div>
              <div>
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">Data Research</div>
                <div className="text-xs font-bold text-slate-900 dark:text-white">Marketing</div>
                <p className="text-[10px] text-slate-400 mt-1">You marked 5/5 all assignments done!</p>
              </div>
            </div>

            {/* Gauge 2: 65% */}
            <div className="p-4 rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 shadow-sm flex flex-col items-center text-center space-y-2">
              <div className="relative w-14 h-14 flex items-center justify-center">
                <svg className="w-14 h-14 -rotate-90">
                  <circle cx="28" cy="28" r="22" stroke="currentColor" strokeWidth="4" className="text-slate-100 dark:text-slate-800" fill="transparent" />
                  <circle cx="28" cy="28" r="22" stroke="#F43F5E" strokeWidth="4" strokeDasharray="138" strokeDashoffset="48" strokeLinecap="round" fill="transparent" />
                </svg>
                <span className="absolute text-xs font-black text-slate-900 dark:text-white">65%</span>
              </div>
              <div>
                <div className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">UX/UI Design</div>
                <div className="text-xs font-bold text-slate-900 dark:text-white">Typography</div>
                <Button size="sm" onClick={() => toast.success("Marked typography checked")} className="h-6 mt-1.5 px-3 rounded-full text-[10px] font-bold bg-indigo-600 text-white">
                  Check
                </Button>
              </div>
            </div>
          </div>

          {/* Board Meeting Card */}
          <div className="rounded-3xl bg-white dark:bg-[#111827] border border-slate-100 dark:border-slate-800 p-5 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
                <span className="text-xs font-bold text-slate-900 dark:text-white">Board meeting</span>
              </div>
              <Edit2 className="w-3.5 h-3.5 text-slate-400 cursor-pointer" />
            </div>

            <div>
              <div className="text-[11px] font-semibold text-slate-400">March 24 at 4:00 PM</div>
              <p className="text-xs text-slate-700 dark:text-slate-300 font-medium mt-1">
                Meeting with John Smith, 4th floor, room 159
              </p>
            </div>

            <div className="flex items-center gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.info("Rescheduling dialog opened")}
                className="flex-1 h-8 rounded-full text-xs font-semibold border-slate-200 dark:border-slate-700"
              >
                Reschedule
              </Button>
              <Button
                size="sm"
                onClick={() => toast.success("Meeting invitation accepted!")}
                className="flex-1 h-8 rounded-full text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                Accept invite
              </Button>
            </div>
          </div>

        </div>

      </div>

      {/* ── Active Location & GoHighLevel Sub-Account Bar ── */}
      <Card
        className="p-5 sm:p-6 border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#111827] shadow-sm rounded-3xl"
        data-testid="dashboard-location-bar"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-900/60 flex items-center justify-center text-indigo-600 shrink-0">
              <MapPin className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  Active Account &amp; Location
                </span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400">
                  Live Sync
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <h3 className="text-xl font-bold text-slate-900 dark:text-white">
                  {activeLocation.name}
                </h3>
                <span className="text-slate-400">·</span>
                <span className="text-xs sm:text-sm text-slate-600 dark:text-slate-400 font-medium">
                  {activeLocation.city}, {activeLocation.state}
                </span>
                <span className="hidden sm:inline text-xs text-slate-400">({activeLocation.category})</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Quick Switch Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="h-10 px-4 rounded-xl gap-2 text-xs font-semibold border-slate-200 dark:border-slate-700">
                  <span>Switch Location ({locations.length})</span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Select Location</DropdownMenuLabel>
                {locations.map((loc) => {
                  const isActive = activeLocation.id === loc.id;
                  return (
                    <DropdownMenuItem
                      key={loc.id}
                      onClick={() => switchLocation(loc.id)}
                      className={`cursor-pointer flex items-center justify-between ${
                        isActive ? "bg-slate-100 dark:bg-slate-800 font-bold" : ""
                      }`}
                    >
                      <div>
                        <div className="text-xs font-semibold">{loc.name}</div>
                        <div className="text-[10px] text-slate-400">{loc.city}, {loc.state}</div>
                      </div>
                      {isActive && <Check className="w-3.5 h-3.5 text-blue-600" />}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Create New Location Modal Trigger */}
            <Button
              size="sm"
              onClick={() => setCreateLocOpen(true)}
              className="h-10 px-5 rounded-xl gap-2 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white shadow-xs"
            >
              <Plus className="w-4 h-4" /> + Create New Location
            </Button>
          </div>
        </div>
      </Card>

      {/* ── INTERACTIVE MODALS ── */}

      {/* 1. Add New Board Modal */}
      <Dialog open={addBoardOpen} onOpenChange={setAddBoardOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderPlus className="w-5 h-5 text-indigo-600" />
              <span>Create New Workspace Board</span>
            </DialogTitle>
            <DialogDescription>
              Create a board to organize your creative notes, sprint tasks, or client deliverables.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddBoard} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Board Name</Label>
              <Input
                value={boardTitle}
                onChange={(e) => setBoardTitle(e.target.value)}
                placeholder="e.g. Q4 Growth Sprint or Product Packaging"
                className="mt-1 text-xs"
                required
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Board Category</Label>
              <select
                value={boardType}
                onChange={(e) => setBoardType(e.target.value)}
                className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-slate-800 px-3 text-xs bg-transparent"
              >
                <option value="Marketing Campaigns">Marketing Campaigns</option>
                <option value="Packaging & Design">Packaging &amp; Design</option>
                <option value="Client Onboarding">Client Onboarding</option>
                <option value="Automations & CRM">Automations &amp; CRM</option>
              </select>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setAddBoardOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                Create Board
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 2. Stay Organized Modal */}
      <Dialog open={stayOrganizedOpen} onOpenChange={setStayOrganizedOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-indigo-600" />
              <span>Stay Organized · Note Structure</span>
            </DialogTitle>
            <DialogDescription>
              A clear structure for all your agency ideas, meeting transcripts, and project notes.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs text-slate-600 dark:text-slate-300">
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Auto-tagging &amp; AI categorisation</span>
              </div>
              <Badge className="bg-emerald-500 text-white text-[9px]">Enabled</Badge>
            </div>
            <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                <span>Sub-folder hierarchical nesting</span>
              </div>
              <Badge className="bg-emerald-500 text-white text-[9px]">Active</Badge>
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={() => setStayOrganizedOpen(false)} className="bg-indigo-600 text-white">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 3. Sync Your Notes Modal */}
      <Dialog open={syncNotesOpen} onOpenChange={setSyncNotesOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
              <span>Cloud Sync Engine</span>
            </DialogTitle>
            <DialogDescription>
              Ensure all boards and notes are synchronized across team devices in real-time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 text-xs">
            <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 text-emerald-700 dark:text-emerald-300 flex items-center justify-between">
              <span>Hostinger Cloud VPS Sync</span>
              <span className="font-bold">Active · 100% Synced</span>
            </div>
            <div className="text-slate-500 text-[11px]">
              Last synchronization occurred 2 seconds ago. Changes auto-save continuously.
            </div>
          </div>
          <DialogFooter>
            <Button size="sm" onClick={() => {
              toast.success("Manual force sync completed!");
              setSyncNotesOpen(false);
            }} className="bg-indigo-600 text-white">
              Force Sync Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 4. Collaborate and Share Modal */}
      <Dialog open={collaborateOpen} onOpenChange={setCollaborateOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-indigo-600" />
              <span>Collaborate &amp; Share Notes</span>
            </DialogTitle>
            <DialogDescription>
              Invite colleagues and team members to review or edit your boards.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => {
            e.preventDefault();
            if (!inviteEmail) return;
            toast.success(`Invitation link sent to ${inviteEmail}`);
            setCollaborateOpen(false);
            setInviteEmail("");
          }} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Colleague Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@agency.com"
                className="mt-1 text-xs"
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" size="sm" onClick={() => setCollaborateOpen(false)}>Cancel</Button>
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                Send Invite
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 5. Add Assignment Modal */}
      <Dialog open={addAssignmentOpen} onOpenChange={setAddAssignmentOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-indigo-600" />
              <span>Add New Assignment</span>
            </DialogTitle>
            <DialogDescription>
              Create an assignment card with custom priority and tags.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddAssignment} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Assignment Title</Label>
              <Input
                value={assignmentTitle}
                onChange={(e) => setAssignmentTitle(e.target.value)}
                placeholder="e.g. Design packaging concept or Review funnel"
                className="mt-1 text-xs"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Category Tag</Label>
                <Input
                  value={assignmentTag}
                  onChange={(e) => setAssignmentTag(e.target.value)}
                  placeholder="e.g. Package design"
                  className="mt-1 text-xs"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">Priority</Label>
                <select
                  value={assignmentPriority}
                  onChange={(e) => setAssignmentPriority(e.target.value)}
                  className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-slate-800 px-3 text-xs bg-transparent"
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setAddAssignmentOpen(false)}>Cancel</Button>
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                Add Assignment
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* 6. Location Dialog */}
      <Dialog open={createLocOpen} onOpenChange={setCreateLocOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-indigo-600" />
              <span>Create New Location / Sub-Account</span>
            </DialogTitle>
            <DialogDescription>
              Provision an isolated sub-account with dedicated funnels, contacts, and reputation pipelines.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateDashboardLocation} className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Location / Studio Name</Label>
              <Input
                value={locForm.name}
                onChange={(e) => setLocForm({ ...locForm, name: e.target.value })}
                placeholder="e.g. Colibri Studio Austin or RealHUB Miami"
                className="mt-1 text-xs"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">City</Label>
                <Input
                  value={locForm.city}
                  onChange={(e) => setLocForm({ ...locForm, city: e.target.value })}
                  placeholder="e.g. Austin"
                  className="mt-1 text-xs"
                  required
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase text-slate-500">State / Region</Label>
                <Input
                  value={locForm.state}
                  onChange={(e) => setLocForm({ ...locForm, state: e.target.value })}
                  placeholder="e.g. Texas"
                  className="mt-1 text-xs"
                  required
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase text-slate-500">Business Category</Label>
              <select
                value={locForm.category}
                onChange={(e) => setLocForm({ ...locForm, category: e.target.value })}
                className="w-full mt-1 h-9 rounded-md border border-slate-200 dark:border-slate-800 px-3 text-xs bg-transparent"
              >
                <option value="Commercial Photography & Media">Commercial Photography & Media</option>
                <option value="Full-Service Marketing Agency">Full-Service Marketing Agency</option>
                <option value="Hospitality & Event Venue">Hospitality & Event Venue</option>
                <option value="Local Real Estate & Construction">Local Real Estate & Construction</option>
                <option value="Professional Services">Professional Services</option>
                <option value="E-Commerce & Retail">E-Commerce & Retail</option>
                <option value="Technology & Software / SaaS">Technology & Software / SaaS</option>
                <option value="Education & Coaching">Education & Coaching</option>
                <option value="Other">Other / Custom Category</option>
              </select>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setCreateLocOpen(false)}>Cancel</Button>
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold">
                Create &amp; Switch
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
